import { api } from "./client";
import type { ApiResponse } from "./types";

export type HouseholdRole = "owner" | "member";

/** What one member reads about another: city and roots, never an email address. */
export interface MemberCard {
  id: string;
  name: string;
  avatarUrl: string | null;
  city: string | null;
  state: string | null;
  roots: { code: string; label: string }[];
}

export interface HouseholdMember {
  userId: string;
  role: HouseholdRole;
  joinedAt: string;
  user: MemberCard;
}

export interface HouseholdInviteLink {
  token: string;
  url: string;
  expiresAt: string;
  joinCount: number;
  maxJoins: number;
  createdAt: string;
}

export interface Household {
  id: string;
  name: string | null;
  createdAt: string;
  members: HouseholdMember[];
  /** The open join link. The owner reads it; a member always reads null. */
  inviteLink: HouseholdInviteLink | null;
}

export interface PendingInvite {
  id: string;
  expiresAt: string;
  createdAt: string;
  household: { id: string; name: string | null };
  invitedBy: MemberCard;
}

/** An invite the household sent that is still waiting. The owner only. */
export interface SentHouseholdInvite {
  id: string;
  expiresAt: string;
  createdAt: string;
  invitee: { id: string; name: string; avatarUrl: string | null };
  invitedBy: { id: string; name: string };
}

export interface MyHousehold {
  household: Household | null;
  myRole: HouseholdRole | null;
  pendingInvites: PendingInvite[];
  /** The owner only; null for anyone else. */
  sentInvites: SentHouseholdInvite[] | null;
  /**
   * What the caller may do now, from the server's own rules: create, rename,
   * invite, withdraw_invite, share_invite_link, revoke_invite_link,
   * remove_member, hand_over, dissolve, leave. Open words: ignore one you do not know.
   */
  allowedActions: string[];
}

/** The inviter or the owner takes back an invite nobody answered. Silent; 409 once answered. */
export function withdrawHouseholdInvite(inviteId: string) {
  return api.delete<void>(`/households/invites/${inviteId}`);
}

export function getMyHousehold() {
  return api.get<ApiResponse<MyHousehold>>("/households/me");
}

export function createHousehold(name?: string) {
  return api.post<ApiResponse<Household>>("/households", name ? { name } : {});
}

export function inviteMember(inviteeUserId: string) {
  return api.post<
    ApiResponse<{
      id: string;
      inviteeUserId: string;
      status: string;
      expiresAt: string;
      createdAt: string;
    }>
  >("/households/invites", { inviteeUserId });
}

export function respondToInvite(inviteId: string, action: "accept" | "decline") {
  return api.post<
    ApiResponse<{
      inviteId: string;
      status: "accepted" | "declined";
      household: Household | null;
    }>
  >(`/households/invites/${inviteId}/respond`, { action });
}

export function removeMember(userId: string) {
  return api.delete<ApiResponse<{ removed: true; userId: string }>>(
    `/households/members/${userId}`,
  );
}

/** The owner gives the household a new name. */
export function renameHousehold(name: string) {
  return api.patch<ApiResponse<Household>>("/households/me", { name });
}

/** The owner hands the household to another member, who is the owner at once. */
export function handOverHousehold(userId: string) {
  return api.post<ApiResponse<Household>>(`/households/members/${userId}/hand-over`, {});
}

export function dissolveHousehold() {
  return api.delete<void>("/households/me");
}

export function createInviteLink() {
  return api.post<ApiResponse<HouseholdInviteLink>>("/households/invite-link", {});
}

export function revokeInviteLink() {
  return api.delete<void>("/households/invite-link");
}
